import { AnimatePresence, MotiView } from "moti";
import { StyleSheet, TextInput, View } from "react-native";
import { Bell, ChevronLeft, ChevronRight, Clock3 } from "lucide-react";

import CustomButton from "../../../../../components/common/general/CustomButton/CustomButton";
import CustomText from "../../../../../components/common/general/CustomText/CustomText";
import { colors, radii, spacing, typographyScale } from "../../../../../styles/design-system";
import {
  BANNER_TRANSITION_DURATION_MS,
  CUSTOM_SNOOZE_OPTION,
  CUSTOM_SNOOZE_WEEKDAY_LABELS,
  OPTIONS_TRANSITION_DURATION_MS,
  type PresetSnoozeOption,
  isSameDay,
  useSnoozeState,
} from "../hooks/useSnoozeState";

type CustomSnoozePopoverProps = {
  activeSnooze: PresetSnoozeOption | "Custom..." | null;
  snoozeAllUntil: string | null;
  animationsEnabled: boolean;
  onUserInteraction: () => void;
  onPresetSnoozeSelect: (option: PresetSnoozeOption) => Promise<void>;
  onCustomSnoozeSelect: (snoozeUntilIso: string) => Promise<boolean>;
  onResumeSnooze: () => Promise<void>;
};

export const CustomSnoozePopover = ({
  activeSnooze,
  snoozeAllUntil,
  animationsEnabled,
  onUserInteraction,
  onPresetSnoozeSelect,
  onCustomSnoozeSelect,
  onResumeSnooze,
}: CustomSnoozePopoverProps) => {
  const {
    showCustomSnooze,
    customSnoozeDate,
    customSnoozeHours,
    customSnoozeMinutes,
    monthLabel,
    today,
    maxDate,
    calendarDays,
    canConfirmCustomSnooze,
    hasActiveSnooze,
    shouldAnimate,
    activeSnoozeUntilLabel,
    customSnoozeAnchorRef,
    minutesInputRef,
    setCustomSnoozeDate,
    handleCustomSnoozeToggle,
    handleCustomConfirm,
    handlePresetSnoozePress,
    handleResumeSnoozePress,
    handlePreviousMonth,
    handleNextMonth,
    handleCustomSnoozeHoursChange,
    handleCustomSnoozeMinutesChange,
  } = useSnoozeState({
    animationsEnabled,
    snoozeAllUntil,
    onUserInteraction,
    onPresetSnoozeSelect,
    onCustomSnoozeSelect,
    onResumeSnooze,
  });

  return (
    <View style={styles.container}>
      <View style={styles.buttonRow}>
        <AnimatePresence>
          {hasActiveSnooze ? (
            <MotiView
              key="snooze-active"
              from={shouldAnimate ? { opacity: 0, height: 0 } : undefined}
              animate={{ opacity: 1, height: 62 }}
              exit={shouldAnimate ? { opacity: 0, height: 0 } : undefined}
              transition={{
                type: "timing",
                duration: shouldAnimate ? BANNER_TRANSITION_DURATION_MS : 0,
              }}
              style={styles.bannerAnimatedContainer}>
              <View style={styles.activeSnoozeBanner}>
                <View style={styles.activeSnoozeContent}>
                  <View style={styles.activeSnoozeDetails}>
                    <Bell size={14} color={colors.warning[700]} aria-hidden={true} />
                    <CustomText
                      text={`Snoozed until ${activeSnoozeUntilLabel}`}
                      useTranslationText={false}
                      style={styles.activeSnoozeText}
                    />
                  </View>

                  <CustomButton
                    text="Resume now"
                    useTranslationText={false}
                    styleType="transparent"
                    style={styles.resumeButton}
                    textStyle={styles.resumeButtonText}
                    onPress={handleResumeSnoozePress}
                    accessibilityLabel="Resume notifications now"
                  />
                </View>
              </View>
            </MotiView>
          ) : (
            <MotiView
              key="snooze-options"
              from={shouldAnimate ? { opacity: 0 } : undefined}
              animate={{ opacity: 1 }}
              exit={shouldAnimate ? { opacity: 0 } : undefined}
              transition={{
                type: "timing",
                duration: shouldAnimate ? OPTIONS_TRANSITION_DURATION_MS : 0,
              }}
              style={styles.optionsAnimatedContainer}>
              {(["1 hour", "1 week"] as const).map((option) => (
                <CustomButton
                  key={option}
                  text={option}
                  useTranslationText={false}
                  styleType="transparent"
                  style={styles.snoozeButton}
                  textStyle={styles.snoozeButtonText}
                  onPress={() => {
                    handlePresetSnoozePress(option);
                  }}
                  accessibilityLabel={`Snooze notifications for ${option}`}
                />
              ))}

              <View style={styles.customSnoozeAnchor} ref={customSnoozeAnchorRef}>
                <CustomButton
                  text={CUSTOM_SNOOZE_OPTION}
                  useTranslationText={false}
                  styleType="transparent"
                  style={[styles.snoozeButton, activeSnooze === CUSTOM_SNOOZE_OPTION && styles.snoozeButtonActive]}
                  textStyle={[
                    styles.snoozeButtonText,
                    activeSnooze === CUSTOM_SNOOZE_OPTION && styles.snoozeButtonTextActive,
                  ]}
                  onPress={handleCustomSnoozeToggle}
                  accessibilityLabel="Snooze notifications with custom date and time"
                />

                {showCustomSnooze ? (
                  <View style={styles.popoverContent}>
                    <View style={styles.calendarHeader}>
                      <CustomButton
                        text=""
                        useTranslationText={false}
                        styleType="transparent"
                        style={styles.monthNavButton}
                        leftIcon={<ChevronLeft size={16} color={colors.grey[700]} />}
                        onPress={handlePreviousMonth}
                        accessibilityLabel="Previous month"
                      />

                      <CustomText text={monthLabel} useTranslationText={false} style={styles.monthLabel} />

                      <CustomButton
                        text=""
                        useTranslationText={false}
                        styleType="transparent"
                        style={styles.monthNavButton}
                        leftIcon={<ChevronRight size={16} color={colors.grey[700]} />}
                        onPress={handleNextMonth}
                        accessibilityLabel="Next month"
                      />
                    </View>

                    <View style={styles.weekdayRow}>
                      {CUSTOM_SNOOZE_WEEKDAY_LABELS.map((weekday) => (
                        <View key={weekday} style={styles.weekdayCell}>
                          <CustomText text={weekday} useTranslationText={false} style={styles.weekdayText} />
                        </View>
                      ))}
                    </View>

                    <View style={styles.calendarGrid}>
                      {calendarDays.map((day, index) => {
                        if (!day) {
                          return <View key={`empty-${index}`} style={styles.calendarDayCell} />;
                        }

                        const isDisabled = day.getTime() < today.getTime() || day.getTime() > maxDate.getTime();
                        const isSelected = customSnoozeDate ? isSameDay(day, customSnoozeDate) : false;

                        return (
                          <CustomButton
                            key={day.toISOString()}
                            text={`${day.getDate()}`}
                            useTranslationText={false}
                            styleType="transparent"
                            disabled={isDisabled}
                            style={[styles.calendarDayCell, isSelected && styles.calendarDayCellSelected]}
                            textStyle={[
                              styles.calendarDayText,
                              isDisabled && !isSelected && styles.calendarDayTextDisabled,
                              isSelected && styles.calendarDayTextSelected,
                            ]}
                            onPress={() => {
                              setCustomSnoozeDate(day);
                            }}
                            accessibilityLabel={`Select ${day.toDateString()}`}
                          />
                        );
                      })}
                    </View>

                    <View style={styles.popoverDivider} />

                    <View style={styles.timeGroup}>
                      <CustomText text="Snooze until" useTranslationText={false} style={styles.timeLabel} />
                      <View style={styles.timeInputGroup}>
                        <TextInput
                          value={customSnoozeHours}
                          onChangeText={handleCustomSnoozeHoursChange}
                          placeholder="HH"
                          keyboardType="number-pad"
                          accessibilityLabel="Snooze hour in HH"
                          maxLength={2}
                          selectTextOnFocus
                          style={[styles.timeInput, styles.timeInputSegment]}
                        />
                        <CustomText text=":" useTranslationText={false} style={styles.timeSeparator} />
                        <TextInput
                          ref={minutesInputRef}
                          value={customSnoozeMinutes}
                          onChangeText={handleCustomSnoozeMinutesChange}
                          placeholder="mm"
                          keyboardType="number-pad"
                          accessibilityLabel="Snooze minutes in mm"
                          maxLength={2}
                          selectTextOnFocus
                          style={[styles.timeInput, styles.timeInputSegment]}
                        />
                        <View style={styles.timeIconAddon}>
                          <Clock3 size={14} color={colors.grey[500]} aria-hidden={true} />
                        </View>
                      </View>
                    </View>

                    <CustomButton
                      text="Confirm snooze"
                      useTranslationText={false}
                      styleType="transparent"
                      style={[styles.confirmButton, !canConfirmCustomSnooze && styles.confirmButtonDisabled]}
                      textStyle={styles.confirmButtonText}
                      disabled={!canConfirmCustomSnooze}
                      onPress={() => {
                        void handleCustomConfirm();
                      }}
                      accessibilityLabel="Confirm custom snooze"
                    />
                  </View>
                ) : null}
              </View>
            </MotiView>
          )}
        </AnimatePresence>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginTop: spacing.xs,
    overflow: "visible",
  },
  buttonRow: {
    flexDirection: "row",
    gap: spacing.xs,
    flexWrap: "wrap",
    overflow: "visible",
  },
  bannerAnimatedContainer: {
    width: "100%",
    overflow: "hidden",
  },
  optionsAnimatedContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: spacing.xs,
    overflow: "visible",
    alignItems: "center",
  },
  activeSnoozeBanner: {
    width: "100%",
    height: 62,
    borderWidth: 1,
    borderColor: "rgb(253, 230, 138)",
    backgroundColor: "rgb(255, 251, 235)",
    borderRadius: radii.full,
  },
  activeSnoozeContent: {
    margin: 12,
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: spacing.xs,
  },
  activeSnoozeDetails: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xxs,
  },
  activeSnoozeText: {
    color: colors.warning[700],
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    fontWeight: "500",
  },
  resumeButton: {
    minHeight: 32,
    borderWidth: 1,
    borderColor: colors.grey[300],
    backgroundColor: colors.grey[100],
    borderRadius: radii.md,
    paddingVertical: 0,
    paddingHorizontal: 12,
    alignItems: "center",
    justifyContent: "center",
    shadowColor: "transparent",
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0,
    shadowRadius: 0,
    elevation: 0,
  },
  resumeButtonText: {
    color: colors.grey[700],
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    fontWeight: "500",
  },
  snoozeButton: {
    borderWidth: 1,
    borderColor: colors.grey[300],
    backgroundColor: colors.grey[50],
    borderRadius: radii.sm,
    minHeight: spacing.xl,
    paddingVertical: 0,
    paddingHorizontal: spacing.xs,
    justifyContent: "center",
    shadowColor: "transparent",
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0,
    shadowRadius: 0,
    elevation: 0,
  },
  snoozeButtonActive: {
    borderColor: colors.brand[500],
    backgroundColor: colors.brand[500],
  },
  snoozeButtonText: {
    color: colors.grey[600],
    fontSize: typographyScale.textXs.fontSize,
    lineHeight: typographyScale.textXs.lineHeight,
    fontWeight: "500",
  },
  snoozeButtonTextActive: {
    color: colors.white,
  },
  customSnoozeAnchor: {
    position: "relative",
    overflow: "visible",
  },
  popoverContent: {
    position: "absolute",
    bottom: 40,
    left: spacing.none,
    width: 256,
    backgroundColor: colors.white,
    borderRadius: radii.md,
    borderWidth: 1,
    borderColor: colors.grey[200],
    paddingTop: 12,
    paddingHorizontal: 12,
    paddingBottom: 12,
    shadowColor: colors.black,
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.12,
    shadowRadius: 18,
    elevation: 30,
    zIndex: 9000,
  },
  calendarHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: spacing.xs,
  },
  monthNavButton: {
    width: spacing.md,
    height: spacing.md,
    padding: 0,
    borderRadius: radii.sm,
    alignItems: "center",
    justifyContent: "center",
    shadowColor: "transparent",
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0,
    shadowRadius: 0,
    elevation: 0,
  },
  monthLabel: {
    color: colors.grey[800],
    fontSize: typographyScale.textBase.fontSize,
    lineHeight: typographyScale.textBase.lineHeight,
    fontWeight: "500",
  },
  weekdayRow: {
    flexDirection: "row",
    marginBottom: spacing.xxs,
  },
  weekdayCell: {
    width: "14.285714%",
    alignItems: "center",
  },
  weekdayText: {
    color: colors.grey[700],
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    fontWeight: "400",
  },
  calendarGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginBottom: spacing.xs,
  },
  calendarDayCell: {
    width: "14.285714%",
    height: spacing.lg,
    padding: 0,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: radii.sm,
    shadowColor: "transparent",
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0,
    shadowRadius: 0,
    elevation: 0,
  },
  calendarDayCellSelected: {
    backgroundColor: colors.brand[100],
  },
  calendarDayText: {
    color: colors.grey[700],
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    fontWeight: "400",
  },
  calendarDayTextDisabled: {
    color: colors.grey[300],
  },
  calendarDayTextSelected: {
    color: colors.brand[700],
    fontWeight: "600",
  },
  popoverDivider: {
    height: 1,
    marginHorizontal: -12,
    marginBottom: spacing.xs,
    backgroundColor: colors.grey[200],
  },
  timeGroup: {
    gap: spacing.xxs,
  },
  timeLabel: {
    color: colors.grey[700],
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    fontWeight: "500",
  },
  timeInputGroup: {
    minHeight: spacing.xl,
    borderRadius: radii.md,
    borderWidth: 1,
    borderColor: colors.grey[300],
    backgroundColor: colors.white,
    paddingHorizontal: spacing.xs,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
  },
  timeInput: {
    color: colors.grey[700],
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    paddingVertical: spacing.xs,
    textAlign: "center",
  },
  timeInputSegment: {
    width: 32,
    paddingHorizontal: spacing.xxs,
  },
  timeSeparator: {
    color: colors.grey[700],
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    marginHorizontal: spacing.xxs,
  },
  timeIconAddon: {
    marginLeft: "auto",
    alignItems: "center",
    justifyContent: "center",
  },
  confirmButton: {
    marginTop: spacing.xs,
    minHeight: 36,
    borderRadius: radii.md,
    backgroundColor: colors.brand[500],
    paddingVertical: 0,
    paddingHorizontal: spacing.sm,
    alignItems: "center",
    justifyContent: "center",
    shadowColor: "transparent",
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0,
    shadowRadius: 0,
    elevation: 0,
  },
  confirmButtonDisabled: {
    backgroundColor: colors.grey[300],
  },
  confirmButtonText: {
    color: colors.white,
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    fontWeight: "600",
  },
});
